import { useState, type FormEvent } from "react";
import { Aviso, Botao, Campo, CampoLongo } from "../ui";
import { Selecionar } from "../sucesso/Selecionar";
import { mensagemDoConsole } from "../../nucleo/console";
import {
  OPCOES_DE_LEMBRETE,
  useApoioDaAgenda,
  validarCompromisso,
  type FormularioDeCompromisso as Formulario,
} from "../../nucleo/compromissos";

export interface VinculoFixo {
  /** Quando a tela já sabe a que se refere (o chamado aberto), os campos de vínculo somem. */
  ticketNumero: number;
}

/**
 * O formulário de um compromisso, para criar e para editar. Na edição o horário não aparece:
 * mudar de hora é "Remarcar", que conta a remarcação e rearma o lembrete.
 */
export function FormularioDeCompromisso({
  inicial,
  editando,
  vinculoFixo,
  enviando,
  erroDoServidor,
  aoEnviar,
  aoCancelar,
}: {
  inicial: Formulario;
  editando: boolean;
  vinculoFixo?: VinculoFixo;
  enviando: boolean;
  erroDoServidor: unknown;
  aoEnviar: (form: Formulario) => void;
  aoCancelar: () => void;
}) {
  const [form, setForm] = useState<Formulario>(inicial);
  const [problema, setProblema] = useState<string | null>(null);
  const apoio = useApoioDaAgenda(form.organizacaoId);

  function enviar(evento: FormEvent) {
    evento.preventDefault();
    const erro = validarCompromisso(form, !editando);
    setProblema(erro);
    if (erro) return;
    aoEnviar(form);
  }

  return (
    <form onSubmit={enviar} noValidate data-testid="formulario-de-compromisso">
      <Campo
        rotulo="Título"
        obrigatorio
        value={form.titulo}
        onChange={(e) => setForm({ ...form, titulo: e.target.value })}
      />
      <CampoLongo
        rotulo="Detalhes"
        value={form.descricao}
        onChange={(e) => setForm({ ...form, descricao: e.target.value })}
      />
      {!editando ? (
        <div className="grid gap-3 md:grid-cols-2">
          <Campo
            rotulo="Começa em"
            type="datetime-local"
            value={form.inicio}
            onChange={(e) => setForm({ ...form, inicio: e.target.value })}
          />
          <Campo
            rotulo="Termina em"
            type="datetime-local"
            value={form.fim}
            onChange={(e) => setForm({ ...form, fim: e.target.value })}
          />
        </div>
      ) : null}
      <div className="grid gap-3 md:grid-cols-2">
        <Selecionar
          rotulo="Responsável"
          value={form.responsavelId}
          onChange={(e) => setForm({ ...form, responsavelId: e.target.value })}
        >
          <option value="">{editando ? "Manter o atual" : "Eu"}</option>
          {(apoio.data?.responsaveis ?? []).map((pessoa) => (
            <option key={pessoa.id} value={pessoa.id}>
              {pessoa.nome}
            </option>
          ))}
        </Selecionar>
        <Selecionar
          rotulo="Lembrete por e-mail"
          value={form.lembreteMinutos}
          onChange={(e) => setForm({ ...form, lembreteMinutos: e.target.value })}
        >
          {OPCOES_DE_LEMBRETE.map((opcao) => (
            <option key={opcao.rotulo} value={opcao.minutos ?? ""}>
              {opcao.rotulo}
            </option>
          ))}
        </Selecionar>
      </div>
      {vinculoFixo === undefined ? (
        <div className="grid gap-3 md:grid-cols-3">
          <Selecionar
            rotulo="Cliente"
            value={form.organizacaoId}
            onChange={(e) => setForm({ ...form, organizacaoId: e.target.value, ativoId: "" })}
          >
            <option value="">Nenhum</option>
            {(apoio.data?.organizacoes ?? []).map((organizacao) => (
              <option key={organizacao.id} value={organizacao.id}>
                {organizacao.nome}
              </option>
            ))}
          </Selecionar>
          <Campo
            rotulo="Chamado (número)"
            inputMode="numeric"
            placeholder="#123"
            value={form.ticketNumero}
            onChange={(e) => setForm({ ...form, ticketNumero: e.target.value })}
          />
          <Selecionar
            rotulo="Ativo"
            value={form.ativoId}
            disabled={!form.organizacaoId}
            onChange={(e) => setForm({ ...form, ativoId: e.target.value })}
          >
            <option value="">{form.organizacaoId ? "Nenhum" : "Escolha o cliente antes"}</option>
            {(apoio.data?.ativos ?? []).map((ativo) => (
              <option key={ativo.id} value={ativo.id}>
                {ativo.nome}
              </option>
            ))}
          </Selecionar>
        </div>
      ) : null}
      {problema ? <Aviso tom="erro" className="mb-3">{problema}</Aviso> : null}
      {erroDoServidor ? <Aviso tom="erro" className="mb-3">{mensagemDoConsole(erroDoServidor)}</Aviso> : null}
      <div className="flex gap-2">
        <Botao type="submit" disabled={enviando} data-testid="salvar-compromisso">
          {editando ? "Salvar alterações" : "Agendar"}
        </Botao>
        <Botao variante="fantasma" onClick={aoCancelar}>
          Cancelar
        </Botao>
      </div>
    </form>
  );
}
